/**
 * ===========================================================================
 * ESCALATION — the pure half. No React, no fetch.
 * ===========================================================================
 *
 * Founder ruling 5 of 2026-09-07 does not describe a button. It describes
 * three answers:
 *
 *   > Exactly one authorized target → route it. Zero → surface "No escalation
 *   > owner configured" and require management resolution. More than one
 *   > equally valid → surface the ambiguity and require a human or
 *   > configuration decision. Never pick probabilistically.
 *
 * THOSE THREE ANSWERS ARE THE UI. A screen that rendered "Escalate" as a
 * button and then said "done" would have hidden which of them happened, and
 * the ladder's whole design is that the refusal is the product. The resolver
 * refuses rather than guesses (`escalation/ladder.ts` has no expression that
 * reduces an eligible set to one); the surface must therefore SHOW the
 * refusal, not swallow it.
 *
 * ===========================================================================
 * FOUR SENTENCES WHERE THE RULING NAMES THREE, AND WHY THE FOURTH IS FORCED
 * ===========================================================================
 * The ruling says "exactly one AUTHORIZED target", not "resolved". A resolved
 * person who may not act on the finding is not a target — which splits "zero"
 * in two, and the two have DIFFERENT REMEDIES:
 *
 *   unconfigured  no rung named anybody. An ABSENCE of configuration.
 *                 Remedy: configure an escalation target.
 *   unauthorized  somebody IS named and not one of them may act. A REFUSAL.
 *                 Remedy: grant the permission, or name somebody else.
 *
 * Collapsing those is the defect the lane contract names — absent versus
 * refused-for-scope — and here it costs a real hour: an administrator reading
 * "No escalation owner configured" while three managers ARE configured would
 * open the configuration screen, find the three rows sitting there, and have
 * no idea what to do next. So every sentence in this file is built from the
 * outcome by a switch with a case per arm; a new outcome is a compile error
 * rather than a silently missing sentence, and a test can hold each one alone.
 *
 * ===========================================================================
 * AND A PERSON'S NAME IS ITS OWN FOUR-STATE PROBLEM — NOW ANSWERED BY THE SERVER
 * ===========================================================================
 * This file used to say both. The escalation view carried bare user ids where
 * `FindingEntity` carried `shown | withheld | absent`, so a surface resolving
 * ids against the directory it could read had no way to tell a candidate it
 * may not be told about from a candidate who is gone. `personLabel` therefore
 * rendered "either outside your directory access or no longer in it" — honest,
 * and useless: a screen that must say both cannot rank, cannot route, and
 * cannot tell a supervisor whether the escalation ladder is broken or merely
 * private to them.
 *
 * The projection now carries `EscalationPerson`, the SAME discriminated union
 * `FindingEntity` carries, resolved on the server by the one implementation
 * that also answers it for a finding's owner. So this file states the TRUE one:
 *
 *   shown     the server named them; render the name.
 *   withheld  they are in this organization and this reader's access does not
 *             cover them. Not gone.
 *   absent    their directory record is gone. Not hidden.
 *   null      NOBODY held the role. A fourth fact again, and the one a
 *             three-state union structurally cannot express.
 *
 * NOTHING HERE CONSULTS A DIRECTORY LIST ANY MORE, and that removal is the
 * point rather than a tidy-up: a client-side index is a SECOND answer to who
 * this reader may be told about, and a second answer is one edit away from
 * disagreeing with the server's. The name a surface renders is now the name
 * the server decided this reader may have.
 */

import type {
  CandidateDisposition,
  Escalation,
  EscalationCandidate,
  EscalationOutcome,
  EscalationPerson,
  EscalationRule,
  EscalationTargetConfig,
  OpenEscalationPage,
  RelationshipVersion,
} from './escalationClient.js';
import type { AuthorityGate } from '../authorityGate.js';

/**
 * WHAT THE QUEUE SHOWS FOR ITS SECOND GATE (`directory:read`) — PH-ADM-002.
 *
 * The route opens on `insights:read` alone, deliberately (see the App.tsx
 * comment this finding is filed against): a reader who holds it and not
 * `directory:read` must reach a screen that explains what is withheld, not a
 * 403 door. That much was already right. What was missing is the distinction
 * `authorityGate` exists to keep: `refused` is a RESOLVED set that does not
 * contain the key — a settled "no" the withheld screen may state as one — and
 * `unresolved`/`unknown` are not that. `EscalationQueue` used to collapse all
 * three into one boolean (`gateOpen(...)` is `false` for all of them) and then
 * asserted the refused sentence — "You do not hold directory access. This is
 * a refusal" — off that boolean, so a reader whose permission set merely
 * COULD NOT BE READ (a failed background refetch of `/auth/me` after
 * `insights:read` was already granted and cached, which is `isError: true`
 * with `has('insights:read')` still `true` — see `authorityGate.ts`'s "allowed
 * CAN FIRE UNDER isError" note) was told a definite "you do not hold this",
 * which may simply be false.
 *
 * `unresolved` maps to the same word as `unknown` ("we could not check")
 * rather than getting its own screen: `RequirePermission` already blocks this
 * component from mounting until the permission set has resolved for
 * `insights:read`, so a caller reaching this function with `unresolved` is a
 * defensive case, not an expected one, and "we could not check" is the true
 * sentence for it too.
 */
export type EscalationDirectoryState = 'ready' | 'unknown' | 'refused';

export function escalationDirectoryState(gate: AuthorityGate): EscalationDirectoryState {
  switch (gate.kind) {
    case 'allowed':
      return 'ready';
    case 'refused':
      return 'refused';
    case 'unresolved':
    case 'unknown':
      return 'unknown';
  }
}

/**
 * What one escalation SAYS, in the reader's language.
 *
 * `remedy` is separate from `detail` because the two are different speech
 * acts: the detail states what happened, the remedy says whose move it is.
 * A screen may render the remedy beside a link and the detail beside the
 * outcome without re-splitting the sentence itself.
 *
 * `tone` lets a screen style "routed" and "nobody is configured" differently
 * without either of them borrowing the other's words.
 */
export interface EscalationSentence {
  readonly outcome: EscalationOutcome;
  readonly headline: string;
  readonly detail: string;
  readonly remedy: string;
  readonly tone: 'settled' | 'open' | 'attention';
  /**
   * Whether the reason nothing routed is a CONFIGURATION ABSENCE, an
   * AUTHORIZATION REFUSAL, an AMBIGUITY, or nothing at all because it routed.
   * Held as its own field so a caller can branch without re-reading the prose,
   * and so a test can assert the four are four.
   */
  readonly reason: 'routed' | 'absent_configuration' | 'refused_for_scope' | 'ambiguous';
}

/**
 * THE FOUR ANSWERS, WORDED SO THAT NO TWO COULD BE MISTAKEN FOR EACH OTHER.
 *
 * `count` is the number the sentence is entitled to state. For `ambiguous` it
 * is the authorized count, not the candidate count: two eligible people and
 * one who may not read the finding is an ambiguity between TWO, and saying
 * three would name somebody as an option who is not one.
 */
export function escalationSentence(escalation: {
  outcome: EscalationOutcome;
  candidateCount: number;
  authorizedCandidateCount: number;
}): EscalationSentence {
  switch (escalation.outcome) {
    case 'routed':
      return {
        outcome: 'routed',
        headline: 'Escalated to one person',
        detail:
          'Exactly one authorized target resolved from your organization’s own records, so it went there.',
        remedy: 'Nothing is waiting on you.',
        tone: 'settled',
        reason: 'routed',
      };
    case 'ambiguous':
      // NEVER "we picked the first one", and never a quiet default. The
      // resolver cannot choose and neither may this sentence.
      return {
        outcome: 'ambiguous',
        headline: 'More than one escalation owner applies',
        detail:
          escalation.authorizedCandidateCount === 2
            ? 'Two people are equally valid escalation owners for this finding. Phibian will not choose between them.'
            : `${escalation.authorizedCandidateCount} people are equally valid escalation owners for this finding. Phibian will not choose between them.`,
        remedy:
          'Choose one of them below, or make one of them more specific in Escalation targets. Until somebody decides, this has not reached anyone.',
        tone: 'open',
        reason: 'ambiguous',
      };
    case 'unconfigured':
      // THE SENTENCE THE RULING NAMES, VERBATIM, as the headline. The server's
      // `ESCALATION_OUTCOME_MESSAGE.unconfigured` is the same string; it is
      // repeated here rather than read off the wire so that a surface still
      // says the right thing about a locally-resolved preview.
      return {
        outcome: 'unconfigured',
        headline: 'No escalation owner configured',
        detail:
          'Nobody is named for this finding at any level — no policy target, no assigned owner to escalate past, nobody in the org chart above its program or site, and no organization fallback role. This is an absence, not a refusal: there is nothing here that you are being kept from seeing.',
        remedy:
          'Somebody with organization settings has to configure an escalation target before this can route.',
        tone: 'attention',
        reason: 'absent_configuration',
      };
    case 'unauthorized':
      // AND THE ONE THAT MUST NOT READ LIKE THE ONE ABOVE IT. Somebody IS
      // named. The organization has answered; the answer cannot act.
      return {
        outcome: 'unauthorized',
        headline: 'No authorized escalation owner',
        detail:
          escalation.candidateCount === 1
            ? 'One person is named for this finding, and they may not act on it. This is a refusal, not an absence — the configuration is there.'
            : `${escalation.candidateCount} people are named for this finding, and not one of them may act on it. This is a refusal, not an absence — the configuration is there.`,
        remedy:
          'Grant the missing access to somebody already named, or name somebody who has it. Configuring another target will not help until one of them can act.',
        tone: 'attention',
        reason: 'refused_for_scope',
      };
  }
}

/** The short form, for a queue row. Still four answers; still never a blank. */
export function outcomeCellLabel(outcome: EscalationOutcome): string {
  switch (outcome) {
    case 'routed':
      return 'Routed';
    case 'ambiguous':
      return 'More than one owner';
    case 'unconfigured':
      return 'Nobody configured';
    case 'unauthorized':
      return 'Named, cannot act';
  }
}

/**
 * WHICH RUNG ANSWERED, worded as a fact about the organization's records.
 *
 * The rung matters to a reader deciding what to change: a target that came
 * from the org chart is fixed by the org chart, and one that came from a
 * configuration is fixed in Escalation targets. `null` is not "unknown" — it
 * is "no rung produced anybody", which only ever accompanies `unconfigured`.
 */
export function ruleSentence(rule: EscalationRule | null): string {
  if (rule === null) {
    return 'No rung of the ladder produced anybody, so no rule selected a target.';
  }
  switch (rule) {
    case 'configured_target':
      return 'Selected by an escalation target configured for this finding’s policy.';
    case 'assigned_owner':
      return 'Selected because this finding has an assigned owner to escalate past.';
    case 'scope_org_graph':
      return 'Selected from your org chart — the manager or supervisor over this finding’s program or site.';
    case 'org_fallback_role':
      return 'Selected from your organization’s fallback escalation role, because nothing more specific applied.';
  }
}

/**
 * WHY A NAMED PERSON IS NOT A TARGET. Two refusals, and they are not the same
 * refusal.
 *
 * `cannot_read_finding` is a DISCLOSURE boundary: routing to them would assert
 * the finding exists, which is the enumeration oracle `finding-reach.ts` was
 * written to close. `no_triage_permission` is an AUTHORITY boundary: they can
 * see it and may not decide about it. Fixing the first is a data-access
 * decision; fixing the second is a permission grant. A screen that said "not
 * eligible" for both would send an administrator to the wrong screen half the
 * time.
 */
export function dispositionSentence(disposition: CandidateDisposition): string {
  switch (disposition) {
    case 'eligible':
      return 'Can act on this finding.';
    case 'cannot_read_finding':
      return 'Cannot be escalated to: this finding is outside what they may see, and telling them it exists would itself disclose it.';
    case 'no_triage_permission':
      return 'Can see this finding and may not decide what happens to it — they are missing the Insights triage permission.';
  }
}

/**
 * WHICH OF THE FOUR THIS PERSON IS. `none` is the null arm — nobody held the
 * role — and it is a different fact from all three disclosure answers, so it
 * gets its own name rather than being folded into `absent`.
 */
export type PersonDisclosure = 'shown' | 'withheld' | 'absent' | 'none';

/**
 * A PERSON, NAMED WHEN THE SERVER SAYS WE MAY AND SPECIFIC WHEN IT SAYS WE MAY
 * NOT.
 *
 * Four answers, four sentences, and no two of them share a distinguishing
 * phrase — which is the property that makes a fixture able to tell them apart
 * and a reader able to act on them:
 *
 *   shown     the name.
 *   withheld  somebody IS there. The remedy is directory access, and the
 *             escalation ladder is not broken.
 *   absent    nobody is there any more. The remedy is to re-point the
 *             escalation, and the ladder IS broken.
 *   none      the role was never held. Nothing to remedy.
 *
 * The two middle answers used to be one sentence saying both. Saying "no longer
 * in the directory" about somebody merely outside a reader's caseload is a
 * false statement about the organization; saying "outside your access" about
 * somebody who has left sends an administrator to ask for a permission that
 * would not have helped.
 *
 * `disclosure` is carried BESIDE the prose rather than left to be recovered
 * from it, so a screen can branch and a test can assert the four are four
 * without string-matching a sentence somebody may reword.
 */
export function personLabel(person: EscalationPerson | null): {
  label: string;
  named: boolean;
  disclosure: PersonDisclosure;
} {
  if (person === null) return { label: 'Nobody', named: false, disclosure: 'none' };
  switch (person.disclosure) {
    case 'shown':
      return { label: person.label, named: true, disclosure: 'shown' };
    case 'withheld':
      return {
        label: 'Someone here your directory access does not cover',
        named: false,
        disclosure: 'withheld',
      };
    case 'absent':
      return {
        label: 'Somebody whose directory record is gone',
        named: false,
        disclosure: 'absent',
      };
  }
}

/**
 * WHERE THE ESCALATION ACTUALLY LANDED WHEN COVERAGE MOVED IT.
 *
 * `resolvedViaUserId` is who the ladder named; `targetUserId` is who it
 * reached. They differ exactly when a live coverage window redirected it, and
 * a surface that showed only one of them would either hide who is now holding
 * the work or hide whose authority chose them. Returns null when there was no
 * redirection, so a caller renders nothing rather than a redundant line.
 */
export function redirectionSentence(
  escalation: Pick<Escalation, 'resolvedVia' | 'target' | 'coverage'>,
): string | null {
  const { resolvedVia, target, coverage } = escalation;
  if (coverage === null) return null;
  if (resolvedVia === null || target === null) return null;
  if (resolvedVia.userId === target.userId) return null;
  const via = personLabel(resolvedVia).label;
  const to = personLabel(target).label;
  return `Your org chart named ${via}; a coverage window in force at the time sent it to ${to} instead.`;
}

/**
 * THE RELATIONSHIP AS IT READ, INCLUDING THE VERSION — ruling 5's own
 * requirement, and the reason a bare relationship id is not enough.
 *
 * Ending a relationship is an UPDATE IN PLACE. An escalation storing only the
 * id becomes unreadable the moment somebody reorganises. So both bounds
 * travel, and `effectiveTo: null` is OPEN-ENDED AS READ — a real answer, not a
 * missing one, and it must not render as "unknown".
 */
export function relationshipSentence(relationship: RelationshipVersion | null): string | null {
  if (relationship === null) return null;
  const scope =
    relationship.scopeType === 'org'
      ? 'organization-wide'
      : `scoped to one ${relationship.scopeType}`;
  const window =
    relationship.effectiveTo === null
      ? `in force from ${relationship.effectiveFrom}, still open`
      : `in force from ${relationship.effectiveFrom} until ${relationship.effectiveTo}`;
  const authority = relationship.authorityBearing
    ? 'an authority-bearing relationship'
    : 'a relationship that carries no authority';
  return `${relationship.kind}, ${scope} — ${authority}, ${window}. That is the version this escalation used.`;
}

/**
 * WHAT THE QUEUE'S TWO NUMBERS EACH MEAN.
 *
 * The server counts the ORGANIZATION's open escalations, then removes the rows
 * whose findings this reader does not reach — narrowing after the count, and
 * saying so. Rendering `total` as if it were the reader's own count would be
 * the "scope applied after pagination" defect, restated on screen. Rendering
 * only `items.length` would hide that there is more.
 */
export function queueScopeSentence(
  page: Pick<OpenEscalationPage, 'total' | 'reachNarrowed'>,
  shown: number,
): string {
  if (!page.reachNarrowed) {
    return page.total === 1
      ? '1 escalation is waiting on somebody.'
      : `${page.total} escalations are waiting on somebody.`;
  }
  if (shown === page.total) {
    return `${shown} shown. Your access may not cover every open escalation in this organization, so this may not be all of them.`;
  }
  return `${shown} of ${page.total} shown. The rest are on findings your access does not cover — they are not resolved, and somebody else has to see them.`;
}

/**
 * A CONFIGURED TARGET, READ BACK AS A SENTENCE.
 *
 * `program` and `location` rank THE SAME on the server, deliberately: a
 * finding carrying both, with a configuration on each, has two equally
 * specific answers. This sentence says which scope a row has so an
 * administrator can see the tie they created rather than discovering it as an
 * ambiguity later.
 */
export function targetConfigSentence(config: EscalationTargetConfig): string {
  const who =
    config.targetUserId !== null
      ? 'a named person'
      : config.targetRoleKey !== null
        ? `whoever holds ${config.targetRoleKey}`
        : 'nobody — this row names no target';
  const what =
    config.level === 'org_fallback'
      ? 'Fallback for anything nothing more specific covers'
      : `${config.policyKind === 'detector' ? 'Detector' : 'Finding type'} ${config.policyKey ?? '—'}`;
  const where =
    config.scopeType === 'org'
      ? 'organization-wide'
      : config.scopeType === 'program'
        ? 'for one program'
        : 'for one site';
  return `${what} → ${who}, ${where}.`;
}

/**
 * THE CANDIDATES A DECISION MAY CHOOSE AMONG — and it is not all of them.
 *
 * The server answers 409 `NotACandidateError` for a target the ladder never
 * resolved, and refuses a candidate that is not eligible by the same
 * reasoning. So the control offers exactly the eligible ones; a picker over
 * the directory would be the invention the ruling forbids, arriving through
 * the door built to close it.
 */
export function decidableCandidates(
  candidates: readonly EscalationCandidate[],
): readonly EscalationCandidate[] {
  return candidates.filter((candidate) => candidate.disposition === 'eligible');
}

/**
 * IS THIS ROW STILL WAITING ON SOMEBODY?
 *
 * A `routed` escalation is finished. An `ambiguous` one that a human has since
 * decided is finished too — `humanDecided` marks the decision that closed it,
 * and a queue that kept showing it would ask for the same decision twice.
 */
export function isOpen(escalation: Pick<Escalation, 'outcome' | 'resolvedAt'>): boolean {
  return escalation.outcome !== 'routed' && escalation.resolvedAt === null;
}
